import axios from 'axios';
import dayjs from 'dayjs';

export type DailyWeather = {
  /** Average daily temperature, °C. */
  temperature: number | null;
  /** Total precipitation, mm. */
  rain: number | null;
};

// Both sources are free for commercial use and need no API key:
// - NASA POWER (https://power.larc.nasa.gov) for past days; it lags
//   2-3 days behind.
// - MET Norway (https://api.met.no, CC BY 4.0) for the coming ~9 days.
const NASA_POWER_URL = 'https://power.larc.nasa.gov/api/temporal/daily/point';
const MET_NORWAY_URL =
  'https://api.met.no/weatherapi/locationforecast/2.0/compact';

const NASA_FILL_VALUE = -999;
const DATE_FORMAT = 'YYYY-MM-DD';

type NasaPowerResponse = {
  properties: {
    parameter: {
      T2M: Record<string, number>;
      PRECTOTCORR: Record<string, number>;
    };
  };
};

type MetNorwayResponse = {
  properties: {
    timeseries: {
      time: string;
      data: {
        instant: { details: { air_temperature?: number } };
        next_1_hours?: { details: { precipitation_amount?: number } };
        next_6_hours?: { details: { precipitation_amount?: number } };
      };
    }[];
  };
};

const toValue = (value: number | undefined) =>
  value === undefined || value <= NASA_FILL_VALUE ? null : value;

const fetchPastWeather = async (
  latitude: number,
  longitude: number,
  start: dayjs.Dayjs,
  end: dayjs.Dayjs
) => {
  const response = await axios.get<NasaPowerResponse>(NASA_POWER_URL, {
    params: {
      parameters: 'T2M,PRECTOTCORR',
      community: 'RE',
      latitude,
      longitude,
      start: start.format('YYYYMMDD'),
      end: end.format('YYYYMMDD'),
      format: 'JSON',
    },
  });

  const { T2M, PRECTOTCORR } = response.data.properties.parameter;
  const weather = new Map<string, DailyWeather>();

  Object.keys(T2M).forEach((day) => {
    const temperature = toValue(T2M[day]);
    const rain = toValue(PRECTOTCORR[day]);

    if (temperature !== null || rain !== null) {
      // NASA POWER dates are YYYYMMDD
      const date = `${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6, 8)}`;

      weather.set(date, {
        temperature,
        rain,
      });
    }
  });

  return weather;
};

/**
 * Daily weather from MET Norway's forecast. The forecast is hourly for the
 * first days and 6-hourly after that, so rain is summed from next_1_hours
 * where present and from next_6_hours otherwise.
 */
const fetchForecastWeather = async (latitude: number, longitude: number) => {
  const response = await axios.get<MetNorwayResponse>(MET_NORWAY_URL, {
    params: {
      lat: latitude.toFixed(4),
      lon: longitude.toFixed(4),
    },
  });

  const days = new Map<string, { temperatures: number[]; rain: number }>();

  response.data.properties.timeseries.forEach(({ time, data }) => {
    const day = dayjs(time).format(DATE_FORMAT);
    const entry = days.get(day) || { temperatures: [], rain: 0 };
    const temperature = data.instant.details.air_temperature;
    const rain = data.next_1_hours
      ? data.next_1_hours.details.precipitation_amount
      : data.next_6_hours?.details.precipitation_amount;

    if (temperature !== undefined) {
      entry.temperatures.push(temperature);
    }

    entry.rain += rain || 0;
    days.set(day, entry);
  });

  const weather = new Map<string, DailyWeather>();

  days.forEach(({ temperatures, rain }, day) =>
    weather.set(day, {
      temperature: temperatures.length
        ? temperatures.reduce((sum, value) => sum + value, 0) /
          temperatures.length
        : null,
      rain,
    })
  );

  return weather;
};

/**
 * Daily average temperature and rain for a location, keyed by YYYY-MM-DD.
 * Days without data (the last 2-3 days, or too far ahead) are missing.
 */
export const getDailyWeather = async (
  latitude: number,
  longitude: number,
  startDate: Date,
  endDate: Date
): Promise<Map<string, DailyWeather>> => {
  const today = dayjs().startOf('day');
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');
  const yesterday = today.subtract(1, 'day');

  const requests: Promise<Map<string, DailyWeather>>[] = [];

  if (!start.isAfter(yesterday)) {
    requests.push(
      fetchPastWeather(
        latitude,
        longitude,
        start,
        end.isBefore(yesterday) ? end : yesterday
      )
    );
  }

  if (!end.isBefore(today)) {
    requests.push(fetchForecastWeather(latitude, longitude));
  }

  const results = await Promise.allSettled(requests);
  const weather = new Map<string, DailyWeather>();

  results.forEach((result) => {
    if (result.status === 'rejected') {
      console.error('Error fetching weather:', result.reason);
      return;
    }

    result.value.forEach((value, day) => {
      if (!dayjs(day).isBefore(start) && !dayjs(day).isAfter(end)) {
        weather.set(day, value);
      }
    });
  });

  return weather;
};
